import type { ProjectMethodologyEnum } from 'gql/graphql';
import { Text } from 'uikit';

import type { IconPropSize } from '@consta/icons/__internal__/src/icons/Icon/helpers';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { FunctionComponent, ReactElement } from 'react';
import { memo, useMemo } from 'react';
import type { Control, UseControllerProps } from 'react-hook-form';

import { FormulaEditorField } from './FormulaEditorField';
import { buildInValidator, EDITOR_DEFAULT_ID, useEditorParseData } from './hooks';

import Styles from './FormulaEditor.module.css';

const defaultTooltip = (
    <Text size="xs" className={Styles.textTooltip}>
        <div>Для вызова списка переменных введите знак @ в поле формулы и выберите раздел</div>
        <div>Для математических действий используйте знаки + - * / ( )</div>
    </Text>
);

const defaultAlertTooltip = (
    <Text size="s" className={Styles.textTooltip}>
        <div>Нет данных о переменных.</div>
        <div>Добавление переменных невозможно.</div>
    </Text>
);

const defaultPlaceholderMessage = `• Для вызова списка переменных введите знак @ в поле формулы и выберите раздел
• Для математических действий используйте знаки + - * / ( ).`;

interface IFormulaEditorProps<TData, TVariables> {
    control: Control<any>;
    getObjectSourceQuery: TypedDocumentNode<TData, TVariables>;
    queryVariables?: TVariables;
    name?: string;
    tooltipTitle?: string;
    tooltipTitleClassName?: string;
    placeholderMessage?: string;
    tooltipMessage?: string | ReactElement;
    alertTooltipMessage?: string | ReactElement;
    isDisabled?: boolean;
    isReadOnly?: boolean;
    isRequired?: boolean;
    wrapperClassName?: string;
    isTooltipHidden?: boolean;
    methodology?: ProjectMethodologyEnum;
    sourceDataGetter?: (item: TData) => {
        entityName: string;
        entityChild?: string;
    };
    dataObjModifier?: (items: TData[]) => any[];
    getSelectItemLabel?: (items: string) => any;
    isDotNotationVariables?: boolean;
    isAvailableWithEmptyVariables?: boolean;
    iconTitleSize?: IconPropSize;
    maxIntegerPartLength?: number;
    maxFractionalPartLength?: number;
}

export const FormulaEditor: FunctionComponent<IFormulaEditorProps<any, any> & Pick<UseControllerProps<any>, 'rules'>> =
    memo(
        <TData, TVariables>({
            name = EDITOR_DEFAULT_ID,
            control,
            getObjectSourceQuery,
            queryVariables,
            tooltipTitle,
            tooltipTitleClassName,
            tooltipMessage = defaultTooltip,
            alertTooltipMessage = defaultAlertTooltip,
            placeholderMessage = defaultPlaceholderMessage,
            isDisabled,
            isReadOnly,
            isRequired,
            wrapperClassName,
            rules,
            isTooltipHidden,
            methodology,
            sourceDataGetter,
            dataObjModifier,
            getSelectItemLabel,
            isDotNotationVariables = true,
            isAvailableWithEmptyVariables = false,
            iconTitleSize,
            maxIntegerPartLength = 12,
            maxFractionalPartLength = 6,
        }: IFormulaEditorProps<TData, TVariables> & Pick<UseControllerProps<any>, 'rules'>) => {
            const { isSourceDataLoading, sourceDataForEditor } = useEditorParseData({
                getObjectSourceQuery,
                queryVariables,
                withEffect: true,
                methodology,
                sourceDataGetter,
                dataObjModifier,
            });

            const validationRules = useMemo(
                () => ({
                    validate: buildInValidator(sourceDataForEditor, maxIntegerPartLength, maxFractionalPartLength),
                    ...rules,
                }),

                [rules, sourceDataForEditor, maxIntegerPartLength, maxFractionalPartLength],
            );

            return (
                <FormulaEditorField
                    name={name}
                    control={control}
                    rules={validationRules}
                    objectSource={sourceDataForEditor}
                    wrapperClassName={wrapperClassName}
                    tooltipMessage={tooltipMessage}
                    alertTooltipMessage={alertTooltipMessage}
                    tooltipTitle={tooltipTitle}
                    tooltipTitleClassName={tooltipTitleClassName}
                    placeholderMessage={placeholderMessage}
                    isDisabled={isDisabled}
                    isReadOnly={isReadOnly}
                    isRequired={isRequired}
                    isLoading={isSourceDataLoading}
                    isTooltipHidden={isTooltipHidden}
                    getSelectItemLabel={getSelectItemLabel}
                    isDotNotationVariables={isDotNotationVariables}
                    isAvailableWithEmptyVariables={isAvailableWithEmptyVariables}
                    iconTitleSize={iconTitleSize}
                    maxIntegerPartLength={maxIntegerPartLength}
                    maxFractionalPartLength={maxFractionalPartLength}
                />
            );
        },
    ) as <TData, TVariables>(
        props: IFormulaEditorProps<TData, TVariables> & Pick<UseControllerProps<any>, 'rules'>,
    ) => JSX.Element;

FormulaEditor.displayName = 'FormulaEditor';
